import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { NextPage, NextPageContext } from "next"

import { Card, Form, message } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons"

import Main from "@/templates/Main"
import Meta from "@/layout/Meta"

import { acceptEmailInvitation } from "@/api/users"

import { useSandboxRouter } from "@/hooks/useSandboxRouter"
import { NEW_ORDERS_PAGE_PATH } from "@/constants/routes"

import KosmoButton from "@/UI/Buttons/KosmoButton"
import { ErrorNotification } from "@/UI/Notifications/NotificationTemplate.component"
import { useRecoilValue } from "recoil"
import { userAtom } from "@/atoms/userAtom"
import withAuth from "@/modules/HOC/WithAuth/withAuth"

import { getLock } from "@/modules/shared/AuthLock"
import { useAuth0 } from "@auth0/auth0-react"
import { deleteActiveOrg, setTokens } from "@/utils/localStorage"
import { useUser } from "@/hooks/useUser"
import InvitationTitle from "@/modules/invitation/InvitationTitle"
import ConfirmInvitationFormContent from "@/modules/invitation/ConfirmInvitationFormContent"
import InvitedProfileWarning from "@/modules/invitation/InvitedProfileWarning"
import { useOrg } from "@/hooks/useOrg"

const Invitation: NextPage = () => {
  const { t } = useTranslation()

  const { getAccessTokenSilently } = useAuth0()
  const { fetchUserProfile } = useUser(false)
  const user = useRecoilValue(userAtom)
  const { logout } = useUser()
  const [form] = Form.useForm()
  const [invitationEmail, setInvitationEmail] = useState<string>("")
  const [isConnecting, setIsConnecting] = useState<boolean>(false)
  const [isExistingUser, setIsExistingUser] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const { sandboxRouter } = useSandboxRouter()
  const { switchOrg } = useOrg()

  const sleep = (ms: number) => {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }
  console.log(form.getFieldValue("orgName"))
  const onFinish = async () => {
    setIsLoading(true)
    const urlParams = new URLSearchParams(window.location.search)
    try {
      if (form.getFieldValue("email")) {
        const resp = await acceptEmailInvitation(urlParams.get("inviteID") || "")
        if (resp.status === 200) {
          await sleep(200)
          const response = await fetchUserProfile(form.getFieldValue("orgName"))
          form.getFieldValue("orgName")
          if (response) {
            const targetOrg = response?.data?.orgs?.find(
              (org) => org?.name === form.getFieldValue("orgName")
            )
            if (targetOrg) {
              await switchOrg(targetOrg?.id)
            }
          }
          sandboxRouter.push(NEW_ORDERS_PAGE_PATH)
        }
      }
    } catch (err) {
      console.log(err)
      ErrorNotification("Error", (err as any).message)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    let urlParams = new URLSearchParams(window.location.search)

    form.setFieldsValue({
      orgName: urlParams.get("orgName"),
      name: urlParams.get("name"),
      email: urlParams.get("email"),
    })

    setIsExistingUser(urlParams.get("isUser") === "true")
    setInvitationEmail(urlParams.get("email") || "")
  }, [])

  const handleLock = (
    initialScreen?: "login" | "signUp",
    hide?: boolean,
    isExistingUser?: boolean
  ) => {
    const lock = getLock({
      redirect: false,
      initialScreen: initialScreen || "login",
      prefill: { email: invitationEmail },
      allowSignup: !isExistingUser,
      allowLogin: isExistingUser,
    })
    if (!hide) {
      setIsConnecting(true)
      lock.show()
      lock.on("authenticated", async () => {
        try {
          const accessToken = await getAccessTokenSilently()
          if (accessToken) {
            setTokens({ accessToken: accessToken })
            deleteActiveOrg()

            await fetchUserProfile()

            setIsConnecting(false)
          }
          lock.hide()
          return
        } catch (error) {
          console.log(error)
          message.error("Error authenticating user")
        }
      })
    } else {
      setIsConnecting(false)
      lock.hide()
    }
  }

  const isCorrectUser = invitationEmail === user?.email

  const renderAuthenticatedButtons = () => {
    if (isCorrectUser) {
      return (
        <KosmoButton
          isDisabled={isLoading}
          type="primary"
          classname="w-full h-14 mt-5"
          onClick={form.submit}
          isLoading={isLoading}
        >
          {t("common.continue")}
        </KosmoButton>
      )
    } else {
      return (
        <KosmoButton type="default" classname="w-full h-14 mt-5" onClick={logout}>
          {t("common.Nav.logout")}
        </KosmoButton>
      )
    }
  }

  const renderUnauthenticatedButtons = () => {
    const action = isExistingUser ? "login" : "signUp"
    const buttonText = isExistingUser ? t("login.common.login") : t("signup.common.signup")

    return (
      <KosmoButton
        type="primary"
        classname="w-full h-14 mt-5"
        onClick={() => handleLock(action, undefined, isExistingUser)}
      >
        {buttonText}
      </KosmoButton>
    )
  }

  return (
    <Main
      meta={
        <Meta
          title={t("invitation.Meta.description")}
          description={t("invitation.Meta.description")}
        />
      }
    >
      <div className="w-full" style={{ maxHeight: "100vh", overflowY: "auto" }}>
        <div className="flex flex-col sm:w-full md:w-3/6 xl:w-2/6 mx-auto px-5 items-center">
          <InvitationTitle orgName={form.getFieldValue("orgName")} />
          <div className={`flex flex-col gap-x-2 ${isConnecting ? "block" : "hidden"}`}>
            <div
              onClick={() => handleLock(undefined, true)}
              className="mb-2 place-self-start flex items-center cursor-pointer"
            >
              <FontAwesomeIcon
                style={{ cursor: "pointer" }}
                className="flex-shrink-0 h-5 text-black w-5"
                icon={faArrowLeft}
              />
              <span className="font-medium ml-3">{t("common.back")}</span>
            </div>
            <Card
              className={`h-fit w-fit mt-6 shadow-lg mb-4 xs:mb-20 sm:mb-20 md:mb-20 lg:mb-20 ${
                isConnecting ? "block" : "hidden"
              }`}
              id="auth-container"
            />
          </div>
          <Form
            form={form}
            name="team-people-form"
            onFinish={onFinish}
            style={{ textAlign: "left" }}
            layout="vertical"
            className={`w-full space-y-3 ${isConnecting ? "hidden" : "block"}`}
          >
            <ConfirmInvitationFormContent />
            {!isCorrectUser && <InvitedProfileWarning />}
            <div className="flex gap-x-2">
              {user.id ? renderAuthenticatedButtons() : renderUnauthenticatedButtons()}
            </div>
          </Form>
        </div>
      </div>
    </Main>
  )
}

export default withAuth(Invitation) as any

export async function getStaticProps(context: NextPageContext) {
  if (context?.res) {
    context?.res?.setHeader("Cache-Control", "no-store")
  }
  return {
    props: {},
  }
}
